/** Everything under a project shares its header, tabs, and role context. */

import { createContext, useContext } from "react";
import { NavLink, Outlet, useParams } from "react-router-dom";

import type { ProjectDetail, Role } from "@/api/endpoints";
import { endpoints } from "@/api/endpoints";
import { useAsync } from "@/hooks/useAsync";
import { useSession } from "@/hooks/useSession";
import { Badge, ErrorState, Loading, Notice } from "./ui";

interface ProjectContextValue {
  project: ProjectDetail;
  role: Role | undefined;
  can: (required: Role) => boolean;
  reload: () => void;
}

const ProjectContext = createContext<ProjectContextValue | undefined>(undefined);

export function useProject(): ProjectContextValue {
  const context = useContext(ProjectContext);
  if (!context) throw new Error("useProject must be used inside a project route");
  return context;
}

const TABS = [
  { to: "", label: "Scripts", end: true },
  { to: "runs", label: "Runs" },
  { to: "schedules", label: "Schedules" },
  { to: "repositories", label: "Repositories" },
  { to: "settings", label: "Settings" },
];

export function ProjectLayout() {
  const { projectRef = "" } = useParams();
  const session = useSession();
  const { data, error, loading, reload } = useAsync(
    () => endpoints.project(projectRef),
    [projectRef],
  );

  if (loading) return <Loading rows={3} label="Loading project" />;
  if (error) {
    // A project you cannot see and one that does not exist look identical by design.
    return error.message.includes("not found") || (error as { status?: number }).status === 404 ? (
      <Notice tone="warning" title="Project not found">
        It may have been archived, or you may not have access to it.
      </Notice>
    ) : (
      <ErrorState error={error} onRetry={reload} />
    );
  }
  if (!data) return null;

  const role = session.roleOn(data.id);
  const value: ProjectContextValue = {
    project: data,
    role,
    can: (required) => session.can(data.id, required),
    reload,
  };

  return (
    <ProjectContext.Provider value={value}>
      <div className="page-header">
        <div>
          <h1>{data.name}</h1>
          <div className="page-header__meta row" style={{ gap: 10 }}>
            <span className="mono">{data.slug}</span>
            {role && <Badge tone="accent">{role}</Badge>}
            {data.is_pii && <Badge tone="warning">Customer data</Badge>}
            {data.archived_at && <Badge>Archived</Badge>}
          </div>
        </div>
      </div>

      {data.description && <p className="muted" style={{ marginTop: -8 }}>{data.description}</p>}

      <nav className="tabs">
        {TABS.map((tab) => (
          <NavLink
            key={tab.label}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => `tab${isActive ? " tab--active" : ""}`}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </ProjectContext.Provider>
  );
}
